import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, BookOpen, Edit3, Save, X, AlertTriangle } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { StoryPage } from '@/utils/ai-types';
import { cn } from '@/lib/utils';
import { DEDICATION_MAX_CHARS } from '@/lib/books/dedication';
import { pageTextStatus, type ReadingLevel } from '@/lib/story/reading-level';

/** Live length of a page being edited, against the reading level's limit. Warns only; never rewrites. */
export function PageTextCounter({ text, readingLevel, language }: { text: string; readingLevel: ReadingLevel; language?: string | null }) {
  const s = pageTextStatus(text, readingLevel, language);
  return (
    <p className={cn('text-xs', s.over ? 'text-amber-700' : 'text-gray-500')} aria-live="polite">
      {s.length} / {s.max} {s.unit === 'chars' ? 'characters' : 'words'}
      {s.over && <span> · Long pages may print in smaller text</span>}
    </p>
  );
}

interface StoryPreviewStepProps {
  storyPages: StoryPage[];
  bookTitle: string;
  styleName: string;
  pageCount?: number;
  isMultiCharacter?: boolean;
  feedback?: string;
  onFeedbackChange?: (feedback: string) => void;
  onSubmitFeedback?: () => void;
  isSubmittingFeedback?: boolean;
  onPageContentChange?: (pageNumber: number, newContent: string) => void;
  dedication?: string;
  onDedicationChange?: (v: string) => void;
  dedicationError?: string | null;
  readingLevel?: ReadingLevel;
  language?: string;
}

export function StoryPreviewStep({
  storyPages,
  bookTitle,
  styleName,
  pageCount = 16,
  isMultiCharacter = false,
  feedback = '',
  onFeedbackChange,
  onSubmitFeedback,
  isSubmittingFeedback = false,
  onPageContentChange,
  dedication,
  onDedicationChange,
  dedicationError,
  readingLevel = 'picture',
  language = 'en'
}: StoryPreviewStepProps) {
  const [expandedPages, setExpandedPages] = useState<Set<number>>(
    new Set(storyPages.map((_, index) => index)) // All pages expanded by default
  );
  const [showFeedback, setShowFeedback] = useState(false);
  const [editingPages, setEditingPages] = useState<Record<number, string>>({});
  const [editingPageIndex, setEditingPageIndex] = useState<number | null>(null);
  const [editingDedication, setEditingDedication] = useState<string | null>(null);
  
  const togglePage = (pageIndex: number) => {
    const newExpanded = new Set(expandedPages);
    if (newExpanded.has(pageIndex)) {
      newExpanded.delete(pageIndex);
    } else {
      newExpanded.add(pageIndex);
    }
    setExpandedPages(newExpanded);
  };
  
  // Show only first 2-3 lines of each page when collapsed
  const getPreviewText = (content: string, lines: number = 2) => {
    const sentences = content.split(/[.!?]+/).filter(s => s.trim());
    return sentences.slice(0, lines).join('. ') + (sentences.length > lines ? '...' : '');
  };
  
  // Handle editing a page
  const handleEditPage = (pageIndex: number, content: string) => {
    setEditingPageIndex(pageIndex);
    setEditingPages({ ...editingPages, [pageIndex]: content });
  };
  
  // Save edited page content
  const handleSavePageEdit = (pageIndex: number) => {
    const newContent = editingPages[pageIndex];
    if (newContent !== undefined && onPageContentChange) {
      onPageContentChange(storyPages[pageIndex].pageNumber, newContent);
    }
    setEditingPageIndex(null);
    // Clear the editing state for this page
    const newEditingPages = { ...editingPages };
    delete newEditingPages[pageIndex];
    setEditingPages(newEditingPages);
  };
  
  // Cancel editing
  const handleCancelEdit = () => {
    setEditingPageIndex(null);
    setEditingPages({});
  };
  
  // Calculate book price
  const calculateBookPrice = () => {
    const BASE_PRICE = isMultiCharacter ? 12.94 : 9.95;
    const additionalPages = Math.max(0, pageCount - 8);
    
    if (additionalPages === 0) {
      return BASE_PRICE.toFixed(2);
    }
    
    // Graduated pricing scales
    const basePrices = {
      single: { start: 1.00, end: 0.50 },
      multi: { start: 1.30, end: 0.65 }
    };
    
    const prices = isMultiCharacter ? basePrices.multi : basePrices.single;
    
    // Linear interpolation from 8 to 48 pages
    const startPage = 8;
    const endPage = 48;
    let perPagePrice = prices.start;
    
    if (pageCount > startPage && pageCount < endPage) {
      const progress = (pageCount - startPage) / (endPage - startPage);
      perPagePrice = prices.start - (progress * (prices.start - prices.end));
    } else if (pageCount >= endPage) {
      perPagePrice = prices.end;
    }
    
    const additionalCost = additionalPages * perPagePrice;
    const totalPrice = BASE_PRICE + additionalCost;
    
    return totalPrice.toFixed(2);
  };
  
  return (
    <div className="space-y-6">
      {/* Story header */}
      <div className="text-center py-4">
        <h2 className="text-2xl font-bold mb-2">{bookTitle}</h2>
        <p className="text-gray-600">Style: {styleName}</p>
      </div>
      
      {/* Quality disclaimer - moved to actual preview page */}
      
      {/* Editing Caution Notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-amber-800 mb-1">
              Edit Story Text Carefully
            </p>
            <p className="text-amber-700">
              Please review the story carefully before making edits. While you can modify the text:
            </p>
            <ul className="mt-2 space-y-1 text-xs text-amber-600">
              <li>• Keep edits minimal - major changes may affect story flow</li>
              <li>• Ensure character names remain consistent throughout</li>
              <li>• Maintain age-appropriate language and themes</li>
              <li>• Be aware that text changes won't affect the illustrations</li>
              <li>• Use the feedback option below for larger changes</li>
            </ul>
          </div>
        </div>
      </div>
      
      {/* Dedication page */}
      <div className="border rounded-lg p-4 bg-white" data-testid="dedication-card">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-medium">Dedication page</h3>
          {onDedicationChange && editingDedication === null && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setEditingDedication(dedication ?? '')}
              className="text-purple-600 hover:text-purple-700"
            >
              <Edit3 className="w-4 h-4 mr-1" />
              Edit
            </Button>
          )}
        </div>
        {editingDedication !== null ? (
          <div className="space-y-3">
            <Textarea
              value={editingDedication}
              onChange={(e) => setEditingDedication(e.target.value)}
              className="min-h-[80px] resize-none"
              dir="auto"
              placeholder="For Maya, who makes every day an adventure."
            />
            <p className={cn('text-xs', Array.from(editingDedication).length > DEDICATION_MAX_CHARS ? 'text-red-600' : 'text-gray-500')}>
              {Array.from(editingDedication).length}/{DEDICATION_MAX_CHARS}
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={Array.from(editingDedication).length > DEDICATION_MAX_CHARS}
                onClick={() => { onDedicationChange?.(editingDedication); setEditingDedication(null); }}
              >
                <Save className="w-4 h-4 mr-1" />
                Save
              </Button>
              <Button size="sm" variant="outline" onClick={() => setEditingDedication(null)}>
                <X className="w-4 h-4 mr-1" />
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-gray-700 whitespace-pre-line" dir="auto">
            {dedication?.trim() ? dedication : <span className="text-gray-400 italic">No dedication</span>}
          </p>
        )}
        {dedicationError && <p className="text-xs text-red-600 mt-2">{dedicationError}</p>}
      </div>

      {/* Story pages */}
      <div className="space-y-3">{/* Rest of the component remains the same */}
        {storyPages.map((page, index) => {
          const isExpanded = expandedPages.has(index);
          // Fix: Determine cover page by actual page number, not array index
          const isCoverPage = page.pageNumber === 0;
          const isEditing = editingPageIndex === index;
          const editContent = editingPages[index] ?? page.content;
          
          return (
            <motion.div
              key={index}
              className={cn(
                'border rounded-lg overflow-hidden transition-all',
                isExpanded ? 'shadow-md' : 'shadow-sm hover:shadow-md'
              )}
            >
              <button
                type="button"
                onClick={() => togglePage(index)}
                className={cn(
                  'w-full text-left p-4 flex items-center justify-between transition-colors',
                  isCoverPage ? 'bg-purple-50 hover:bg-purple-100' : 'bg-gray-50 hover:bg-gray-100'
                )}
              >
                <div className="flex items-center gap-3">
                  <BookOpen className={cn(
                    'w-5 h-5',
                    isCoverPage ? 'text-purple-600' : 'text-gray-600'
                  )} />
                  <span className="font-medium">
                    {isCoverPage ? 'Cover Page' : `Page ${page.pageNumber}`}
                  </span>
                </div>
                {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: 'auto' }}
                    exit={{ height: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="p-4 bg-white">
                      {isEditing ? (
                        <div className="space-y-3">
                          <Textarea
                            value={editContent}
                            onChange={(e) => setEditingPages({ ...editingPages, [index]: e.target.value })}
                            className="min-h-[100px] resize-none"
                            placeholder="Enter page content..."
                          />
                          {!isCoverPage && <PageTextCounter text={editContent} readingLevel={readingLevel} language={language} />}
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleSavePageEdit(index)}
                              disabled={!editContent.trim()}
                            >
                              <Save className="w-4 h-4 mr-1" />
                              Save
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={handleCancelEdit}
                            >
                              <X className="w-4 h-4 mr-1" />
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <p className="text-gray-700 leading-relaxed">{page.content}</p>
                          {onPageContentChange && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEditPage(index, page.content)}
                              className="text-purple-600 hover:text-purple-700"
                            >
                              <Edit3 className="w-4 h-4 mr-1" />
                              Edit text
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
                {!isExpanded && (
                  <div className="px-4 pb-3 bg-white">
                    <p className="text-sm text-gray-600 italic">
                      {getPreviewText(page.content)}
                    </p>
                  </div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>
      
      {/* Simplified feedback section */}
      {onFeedbackChange && onSubmitFeedback && (
        <div className="border-t pt-4">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowFeedback(!showFeedback)}
            className="text-purple-600 hover:text-purple-700 mb-3"
          >
            <Edit3 className="w-4 h-4 mr-1" />
            {showFeedback ? 'Hide' : 'Request'} changes
          </Button>
          
          <AnimatePresence>
            {showFeedback && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="space-y-3 overflow-hidden"
              >
                <div className="text-sm text-gray-600 mb-2">
                  <p className="font-medium">Use this for larger changes:</p>
                  <p className="text-xs">If you need significant story modifications, describe them here and we'll regenerate the story for you.</p>
                </div>
                <Textarea
                  value={feedback}
                  onChange={(e) => onFeedbackChange(e.target.value)}
                  placeholder="Describe any changes you'd like..."
                  className="min-h-[80px] resize-none"
                />
                <Button
                  onClick={onSubmitFeedback}
                  disabled={!feedback.trim() || isSubmittingFeedback}
                  size="sm"
                  className="w-full"
                >
                  {isSubmittingFeedback ? 'Updating...' : 'Update Story'}
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
